import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import {
  deleteOperatingCostCampaignCreditFees,
  deleteOperatingCostDefaultCreditFees,
  fetchOperatingCostCreditFees,
  upsertOperatingCostCampaignCreditFees,
  upsertOperatingCostDefaultCreditFees,
  type UpsertOperatingCostCreditInstallmentInput,
} from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCreditFeesPage } from '@/types/api'
import {
  OPERATING_COSTS_CREDIT_CAMPAIGN_DELETED,
  OPERATING_COSTS_CREDIT_CAMPAIGN_SAVED,
  OPERATING_COSTS_CREDIT_DEFAULT_DELETED,
  OPERATING_COSTS_CREDIT_DEFAULT_SAVED,
  OPERATING_COSTS_LOAD_ERROR,
  resolveOperatingCostCreditFeesPage,
  resolveOperatingCostsError,
} from '../lib/operating-costs'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

export type CreditFeesController = {
  data: OperatingCostCreditFeesPage | null
  isLoading: boolean
  isSaving: boolean
  error: string | null
  success: string | null
  saveDefault: (installments: UpsertOperatingCostCreditInstallmentInput[]) => Promise<boolean>
  deleteDefault: () => Promise<boolean>
  saveCampaign: (
    campaignId: number,
    installments: UpsertOperatingCostCreditInstallmentInput[]
  ) => Promise<boolean>
  deleteCampaign: (campaignId: number) => Promise<boolean>
}

type UseCreditFeesOptions = {
  onSaved?: () => Promise<void> | void
}

export function useCreditFees(options: UseCreditFeesOptions = {}): CreditFeesController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const onSavedRef = useRef(options.onSaved)
  onSavedRef.current = options.onSaved
  const [data, setData] = useState<OperatingCostCreditFeesPage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const loadControllerRef = useRef<AbortController | null>(null)
  const isSavingRef = useRef(false)

  const load = useCallback(async (silent = false) => {
    loadControllerRef.current?.abort()
    const controller = new AbortController()
    loadControllerRef.current = controller
    if (!silent) {
      setIsLoading(true)
    }
    setError(null)
    try {
      const response = await runWithStepUpRef.current(() => fetchOperatingCostCreditFees(controller.signal))
      if (controller.signal.aborted) return
      const resolved = resolveOperatingCostCreditFeesPage(response)
      if (!resolved) {
        setData(null)
        setError(OPERATING_COSTS_LOAD_ERROR)
        return
      }
      setData(resolved)
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return
      setData(null)
      setError(resolveOperatingCostsError(err))
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void load()
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [load])

  const runMutation = useCallback(
    async (action: () => Promise<unknown>, successMessage: string): Promise<boolean> => {
      if (isSavingRef.current) {
        return false
      }
      isSavingRef.current = true
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await runWithStepUpRef.current(action)
        setSuccess(successMessage)
        await load(true)
        await onSavedRef.current?.()
        return true
      } catch (err) {
        if (isAbortError(err)) return false
        setError(resolveOperatingCostsError(err))
        return false
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    },
    [load]
  )

  return {
    data,
    isLoading,
    isSaving,
    error,
    success,
    saveDefault: (installments) =>
      runMutation(() => upsertOperatingCostDefaultCreditFees(installments), OPERATING_COSTS_CREDIT_DEFAULT_SAVED),
    deleteDefault: () =>
      runMutation(() => deleteOperatingCostDefaultCreditFees(), OPERATING_COSTS_CREDIT_DEFAULT_DELETED),
    saveCampaign: (campaignId, installments) =>
      runMutation(
        () => upsertOperatingCostCampaignCreditFees(campaignId, installments),
        OPERATING_COSTS_CREDIT_CAMPAIGN_SAVED
      ),
    deleteCampaign: (campaignId) =>
      runMutation(() => deleteOperatingCostCampaignCreditFees(campaignId), OPERATING_COSTS_CREDIT_CAMPAIGN_DELETED),
  }
}
